import React from 'react';
import { 
  AlertDialog as BaseAlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from "@/components/ui/alert-dialog";
import { useHaptics } from "@/hooks/useHaptics";
import { useModal } from "@/hooks/useModal";

interface AdaptiveAlertDialogProps {
  title: string;
  description: string | React.ReactNode;
  cancelText?: string;
  confirmText: string;
  onConfirm: () => void;
  isLoading?: boolean;
  isDestructive?: boolean;
}

interface AdaptiveAlertReturn {
  AlertDialog: React.FC;
  isOpen: boolean;
  openAlert: () => void;
  closeAlert: () => void;
}

export const useAdaptiveAlert = (props: AdaptiveAlertDialogProps): AdaptiveAlertReturn => {
  const { 
    title, 
    description, 
    cancelText = "Cancel", 
    confirmText, 
    onConfirm, 
    isLoading = false,
    isDestructive = false 
  } = props;

  const { lightImpact, errorNotification } = useHaptics();
  const { isOpen, openModal: openAlert, closeModal: closeAlert } = useModal();

  const handleConfirm = () => {
    if (isDestructive) {
      errorNotification();
    } else {
      lightImpact();
    }
    onConfirm();
    closeAlert();
  };

  const handleCancel = () => {
    lightImpact();
    closeAlert();
  };

  const AlertDialog: React.FC = () => {
    return (
      <BaseAlertDialog open={isOpen} onOpenChange={closeAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            <AlertDialogDescription>{description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel} disabled={isLoading}>
              {cancelText}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirm} 
              disabled={isLoading}
              className={isDestructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
            >
              {confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </BaseAlertDialog>
    );
  };

  return {
    AlertDialog,
    isOpen,
    openAlert,
    closeAlert
  };
};

// Direct component version
export const AdaptiveAlertDialog: React.FC<AdaptiveAlertDialogProps & {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ isOpen, onOpenChange, ...props }) => {
  const { lightImpact, errorNotification } = useHaptics();

  const handleConfirm = () => {
    if (props.isDestructive) {
      errorNotification();
    } else {
      lightImpact();
    }
    props.onConfirm();
  };

  const handleCancel = () => {
    lightImpact();
    onOpenChange(false);
  };

  return (
    <BaseAlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{props.title}</AlertDialogTitle>
          <AlertDialogDescription>{props.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel} disabled={props.isLoading}>
            {props.cancelText || "Cancel"}
          </AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirm} 
            disabled={props.isLoading}
            className={props.isDestructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
          >
            {props.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </BaseAlertDialog>
  );
};
